import type { Rng } from '../core/rng/Rng';
import {
  bfsDistances, nearestFloor,
  type GeneratedMap, type Room, type TilePos,
} from './MapGen';
import {
  APERTURE_HI, APERTURE_LO, CELL, E, N, S, W,
  buildVariantIndex, type ChunkVariant,
} from './ChunkDefs';
import { CHUNK_TEMPLATES } from './chunkLibrary';

/**
 * Chunk-stitched floor generation (MAPGEN_PLAN.md): authored templates on a
 * coarse cell grid, seamed via the standard aperture. Behind ?mapgen=chunks
 * (or ?mapgen=<archetype> to force one) until it becomes the default.
 *
 * Each floor draws a layout ARCHETYPE (uniform weights, Sarah 2026-07-23;
 * the legacy `caverns` generator was in the rotation briefly and CUT the
 * same day — noticeably smaller/shorter than chunk floors):
 *   `mixed`   — 2 arena blocks, balanced halls/rooms (the P1 layout);
 *   `warrens` — room-heavy, denser coverage, more loops;
 *   `halls`   — hall-heavy web, most loops, leaner coverage;
 *   `sanctum` — ONE 3×3 giant arena; the floor is an approach + pockets.
 *
 * Skeleton: entrance cap on the grid rim → route through every grand block
 * (nearest-first) → onward to the farthest free cell (so the exit outruns
 * the arenas) → coverage-driven dead-end branches (pockets and grand cells
 * can sprout) → loop edges (ring routes; the legacy serpentine had none).
 * Connectivity is by construction: every template's floor is internally
 * connected (validated) and open edges always seam on the aperture strip.
 *
 * All randomness from the per-floor `map` stream in FIXED draw order:
 *   1. archetype pick (1 draw; forced archetypes still consume it so debug
 *      forcing can't shift the stream)
 *   2. grid dims (2 draws)
 *   3. per-cell routing weight (gw·gh draws)
 *   4. per grand block: position (2 draws; overlap → draw-free scan)
 *   5. entrance rim cell (1 draw)
 *   6. routing — draw-free Dijkstra over the weights from step 3
 *   7. branches until the recipe's coverage target (per branch: start,
 *      direction, length[, dir 2])
 *   8. loops: one accept draw per candidate edge (raster order)
 *   9. per grand, then per carved cell (raster): variant pick (1 draw —
 *      orientation AND recipe kind-weighting are folded into the list)
 *  10. erosion: one draw per candidate wall tile (raster, frozen snapshot)
 *  11. pillars (legacy rules; attempt count scales with room area)
 * Everything after (BFS, exit, shop, candidates) is draw-free.
 */

export type ChunkArchetype = 'mixed' | 'warrens' | 'halls' | 'sanctum';
export const CHUNK_ARCHETYPES: readonly ChunkArchetype[] =
  ['mixed', 'warrens', 'halls', 'sanctum'];

export interface ChunkMap extends GeneratedMap {
  archetype: ChunkArchetype;
}

interface Recipe {
  /** Grand block sizes to place (cells per side each). */
  grandSizes: number[];
  /** Carved-cell fraction the branch pass grows toward. */
  coverage: number;
  loopChance: number;
  /** Variant-pool weights (caps/entries always 1). */
  hallWeight: number;
  roomWeight: number;
}

const RECIPES: Record<ChunkArchetype, Recipe> = {
  mixed: { grandSizes: [2, 2], coverage: 0.8, loopChance: 0.22, hallWeight: 1, roomWeight: 1 },
  warrens: { grandSizes: [2, 2], coverage: 0.9, loopChance: 0.35, hallWeight: 1, roomWeight: 3 },
  halls: { grandSizes: [2, 2], coverage: 0.75, loopChance: 0.4, hallWeight: 4, roomWeight: 1 },
  sanctum: { grandSizes: [3], coverage: 0.7, loopChance: 0.15, hallWeight: 1, roomWeight: 1 },
};

const MARGIN = 1;
/** Erosion odds: concave-corner fill (smooths, never narrows) vs 1-deep dent. */
const ERODE_CORNER = 0.35;
const ERODE_DENT = 0.12;

/** Deterministic module-level expansion of the template library. */
const INDEX = buildVariantIndex(CHUNK_TEMPLATES);

/**
 * Per-archetype weighted mask pools: each variant repeated by its kind's
 * recipe weight, so ONE rng.pick draws from the weighted distribution.
 * Built once, deterministic order — part of the seeded-layout contract.
 */
const WEIGHTED_BY_MASK: ReadonlyMap<string, Map<number, ChunkVariant[]>> = (() => {
  const out = new Map<string, Map<number, ChunkVariant[]>>();
  for (const [arch, r] of Object.entries(RECIPES)) {
    const m = new Map<number, ChunkVariant[]>();
    for (const [mask, list] of INDEX.byMask) {
      const weighted: ChunkVariant[] = [];
      for (const v of list) {
        const w = v.kind === 'hall' ? r.hallWeight : v.kind === 'room' ? r.roomWeight : 1;
        for (let i = 0; i < w; i++) weighted.push(v);
      }
      m.set(mask, weighted);
    }
    out.set(arch, m);
  }
  return out;
})();

interface CellGrid {
  gw: number;
  gh: number;
  /** -1 free · 0..n grand block id. */
  grand: Int8Array;
  carved: Uint8Array;
  /** Open edges as normalized `lo*64+hi` cell-index pairs. */
  open: Set<number>;
}

export function generateChunkMap(rng: Rng, force?: ChunkArchetype): ChunkMap {
  // ---- 1. Archetype (draw ALWAYS consumed; forcing can't shift the stream)
  const drawn = rng.pick(CHUNK_ARCHETYPES);
  const archetype = force ?? drawn;
  const recipe = RECIPES[archetype];

  // ---- 2. Grid dims -------------------------------------------------------
  const gw = rng.intRange(4, 5);
  // ≥20 cells: room for the grand blocks + routes without crowding the frac.
  const gh = gw === 4 ? 5 : rng.intRange(4, 5);
  const w = gw * CELL + 2 * MARGIN;
  const h = gh * CELL + 2 * MARGIN;
  const cells = gw * gh;
  const idx = (gx: number, gy: number): number => gy * gw + gx;

  // ---- 3. Routing weights ------------------------------------------------
  const weights = new Float64Array(cells);
  for (let i = 0; i < cells; i++) weights[i] = rng.range(1, 2.5);

  // ---- 4. Grand block placement -----------------------------------------
  const grid: CellGrid = {
    gw, gh,
    grand: new Int8Array(cells).fill(-1),
    carved: new Uint8Array(cells),
    open: new Set(),
  };
  const grandOrigin: { x: number; y: number; size: number }[] = [];
  for (let g = 0; g < recipe.grandSizes.length; g++) {
    const size = recipe.grandSizes[g]!;
    const wantX = rng.int(gw - size + 1);
    const wantY = rng.int(gh - size + 1);
    // Draw-free forward scan (wrapping) to the first non-overlapping origin.
    let placed = false;
    for (let s = 0; s < cells && !placed; s++) {
      const o = (wantY * gw + wantX + s) % cells;
      const gx = o % gw;
      const gy = (o / gw) | 0;
      if (gx > gw - size || gy > gh - size) continue;
      let free = true;
      for (let dy = 0; dy < size && free; dy++) {
        for (let dx = 0; dx < size; dx++) {
          if (grid.grand[idx(gx + dx, gy + dy)] !== -1) {
            free = false;
            break;
          }
        }
      }
      if (!free) continue;
      for (let dy = 0; dy < size; dy++) {
        for (let dx = 0; dx < size; dx++) {
          grid.grand[idx(gx + dx, gy + dy)] = g;
          grid.carved[idx(gx + dx, gy + dy)] = 1;
        }
      }
      grandOrigin.push({ x: gx, y: gy, size });
      placed = true;
    }
    if (!placed) throw new Error('grand placement failed'); // impossible on ≥4×5
  }

  // ---- 5. Entrance rim cell ---------------------------------------------
  const rim: number[] = [];
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      if (gy !== 0 && gy !== gh - 1 && gx !== 0 && gx !== gw - 1) continue;
      if (grid.grand[idx(gx, gy)] !== -1) continue;
      rim.push(idx(gx, gy));
    }
  }
  const entranceCell = rng.pick(rim);
  grid.carved[entranceCell] = 1;

  // ---- 6. Routing (draw-free) -------------------------------------------
  const grandCells = (g: number): number[] => {
    const out: number[] = [];
    for (let i = 0; i < cells; i++) if (grid.grand[i] === g) out.push(i);
    return out;
  };
  // Chain nearest-first through every grand block (deterministic: distance
  // from the entrance over the seeded weights, then block id).
  const d0 = dijkstra(grid, weights, [entranceCell], entranceCell);
  const order = grandOrigin.map((_, g) => g).sort((a, b) => {
    const da = Math.min(...grandCells(a).map((c) => d0[c]!));
    const db = Math.min(...grandCells(b).map((c) => d0[c]!));
    return da - db || a - b;
  });
  let sources = [entranceCell];
  for (const g of order) {
    routeTo(grid, weights, sources, grandCells(g), entranceCell);
    sources = grandCells(g);
  }
  // Push past the last arena toward the farthest free cell so the deepest
  // room (the exit) is never a grand center.
  const dAll = dijkstra(grid, weights, [entranceCell], -1);
  let farCell = -1;
  let farDist = -1;
  for (let i = 0; i < cells; i++) {
    if (grid.carved[i] === 1 || dAll[i] === Infinity) continue;
    if (dAll[i]! > farDist) {
      farDist = dAll[i]!;
      farCell = i;
    }
  }
  const pocketCells = new Set<number>();
  if (farCell >= 0) {
    routeTo(grid, weights, sources, [farCell], entranceCell);
    pocketCells.add(farCell);
  }

  // ---- 7. Branches (dead-end pockets) -----------------------------------
  // Keep sprouting until the recipe's coverage target: short grand-to-grand
  // routes (adjacent blocks) can leave the skeleton starved otherwise.
  const targetCarved = Math.round(cells * recipe.coverage);
  for (let b = 0; b < cells; b++) {
    let carvedCount = 0;
    for (let i = 0; i < cells; i++) if (grid.carved[i] === 1) carvedCount++;
    if (b >= 2 && carvedCount >= targetCarved) break;
    const starts: number[] = [];
    for (let i = 0; i < cells; i++) {
      // Pockets CAN sprout (they stop being pockets) and so can grand cells
      // (an extra arena side door — seal-down keeps that aperture open).
      if (grid.carved[i] !== 1 || i === entranceCell) continue;
      if (freeNeighbors(grid, i).length > 0) starts.push(i);
    }
    if (starts.length === 0) break;
    let cur = rng.pick(starts);
    const len = rng.intRange(1, 2);
    for (let s = 0; s < len; s++) {
      const free = freeNeighbors(grid, cur);
      if (free.length === 0) break;
      const next = rng.pick(free);
      openEdge(grid, cur, next);
      grid.carved[next] = 1;
      pocketCells.delete(cur); // extended: no longer a dead end
      pocketCells.add(next);
      cur = next;
    }
  }

  // ---- 8. Loops ----------------------------------------------------------
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      const a = idx(gx, gy);
      if (grid.carved[a] !== 1) continue;
      for (const [dx, dy] of [[1, 0], [0, 1]] as const) {
        const nx = gx + dx;
        const ny = gy + dy;
        if (nx >= gw || ny >= gh) continue;
        const b = idx(nx, ny);
        if (grid.carved[b] !== 1 || hasEdge(grid, a, b)) continue;
        // Same-block grand pairs are interior; entrance/pockets stay caps.
        if (grid.grand[a] !== -1 && grid.grand[a] === grid.grand[b]) continue;
        if (a === entranceCell || b === entranceCell) continue;
        if (pocketCells.has(a) || pocketCells.has(b)) continue;
        if (rng.bool(recipe.loopChance)) openEdge(grid, a, b);
      }
    }
  }

  // ---- 9. Stamp chunks ---------------------------------------------------
  const tiles = new Uint8Array(w * h);
  interface Placed {
    room: Room;
    kind: ChunkVariant['kind'];
    cell: number;
    anchors: TilePos[];
  }
  const placed: Placed[] = [];

  for (let g = 0; g < grandOrigin.length; g++) {
    const o = grandOrigin[g]!;
    const pool = INDEX.grands.filter((v) => v.size === o.size);
    const v = rng.pick(pool);
    const ox = MARGIN + o.x * CELL;
    const oy = MARGIN + o.y * CELL;
    stamp(tiles, w, v, ox, oy);
    sealGrand(tiles, w, grid, o, ox, oy);
    placed.push({
      room: toRoom(v, ox, oy),
      kind: 'grand',
      cell: idx(o.x, o.y),
      anchors: v.anchors.map((a) => ({ x: ox + a.x, y: oy + a.y })),
    });
  }

  const maskPool = WEIGHTED_BY_MASK.get(archetype)!;
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      const cell = idx(gx, gy);
      if (grid.carved[cell] !== 1 || grid.grand[cell] !== -1) continue;
      const mask = cellMask(grid, gx, gy);
      const list = cell === entranceCell ? INDEX.entryByMask.get(mask) : maskPool.get(mask);
      if (!list || list.length === 0) throw new Error(`no chunk for mask ${mask}`); // coverage-tested
      const v = rng.pick(list);
      const ox = MARGIN + gx * CELL;
      const oy = MARGIN + gy * CELL;
      stamp(tiles, w, v, ox, oy);
      placed.push({
        room: toRoom(v, ox, oy),
        kind: cell === entranceCell ? 'entry' : pocketCells.has(cell) ? 'cap' : v.kind,
        cell,
        anchors: v.anchors.map((a) => ({ x: ox + a.x, y: oy + a.y })),
      });
    }
  }

  // ---- 10. Erosion (wobble; can never disconnect or breach) --------------
  erode(tiles, w, h, rng);

  // ---- Rooms in contract order: grands, connective, entrance, pockets ----
  const entryPlaced = placed.find((p) => p.kind === 'entry')!;
  const pocketPlaced = placed.filter((p) => p.kind === 'cap');
  const rooms: Room[] = [
    ...placed.filter((p) => p.kind === 'grand').map((p) => p.room),
    ...placed.filter((p) => p.kind !== 'grand' && p.kind !== 'entry' && p.kind !== 'cap').map((p) => p.room),
    entryPlaced.room,
    ...pocketPlaced.map((p) => p.room),
  ];

  // ---- 11. Pillar cover (legacy rules; count scales with area) -----------
  for (const r of rooms) {
    if (r.w * r.h < 260) continue;
    // Chunk rooms run far bigger than legacy chambers (grand 48², plazas
    // ~24²) — the legacy flat 2–4 left them feeling bare (playtest
    // 2026-07-23). Extra attempts still face the same clearance rules.
    const count = rng.intRange(2, 4) + Math.floor((r.w * r.h) / 500);
    for (let i = 0; i < count; i++) {
      const px = rng.intRange(r.x + 4, r.x + r.w - 6);
      const py = rng.intRange(r.y + 4, r.y + r.h - 6);
      let onCenter = false;
      for (const other of rooms) {
        if (Math.abs(px - other.cx) <= 2 && Math.abs(py - other.cy) <= 2) {
          onCenter = true;
          break;
        }
      }
      if (onCenter) continue;
      // 3-tile floor clearance on every side: pillars can't disconnect.
      let clear = true;
      for (let y = py - 3; y <= py + 4 && clear; y++) {
        for (let x = px - 3; x <= px + 4; x++) {
          if (tiles[y * w + x] !== 1) {
            clear = false;
            break;
          }
        }
      }
      if (!clear) continue;
      tiles[py * w + px] = 0;
      tiles[py * w + px + 1] = 0;
      tiles[(py + 1) * w + px] = 0;
      tiles[(py + 1) * w + px + 1] = 0;
    }
  }

  // ---- 12. Draw-free derivation (mirrors legacy section H) ---------------
  const entrance: TilePos = { x: entryPlaced.room.cx, y: entryPlaced.room.cy };
  const dist = bfsDistances(tiles, w, h, entrance.x, entrance.y);

  // Exit = deepest non-grand room center (grands are arenas, never exits —
  // keeps every circle candidate alive by construction).
  let exitRoom = pocketPlaced[0]?.room ?? rooms[0]!;
  let bestFar = -1;
  for (const p of placed) {
    if (p.kind === 'entry' || p.kind === 'grand') continue;
    const d = dist[p.room.cy * w + p.room.cx]!;
    if (d > bestFar) {
      bestFar = d;
      exitRoom = p.room;
    }
  }
  const exit: TilePos = { x: exitRoom.cx, y: exitRoom.cy };

  const shop = nearestFloor(tiles, w, h, entrance.x + 4, entrance.y);

  const circleCandidates = placed
    .filter((p) => p.kind === 'grand')
    .map((p) => ({ x: p.room.cx, y: p.room.cy }))
    .filter((p) => !(p.x === exit.x && p.y === exit.y))
    .sort((a, b) => dist[b.y * w + b.x]! - dist[a.y * w + a.x]!);

  // Event anchors: NEVER from the exit's own chunk (caps hold their exit AND
  // 2 anchors — without this, events pile into the hatch room). Slot 0 stays
  // deepest (the vault door lives in a dead end); the rest are farthest-point
  // ordered so consecutive slots land far apart — rollFloorEvents consumes
  // candidates in order, and pure depth-sorting clustered every event at the
  // deep end of the skeleton (playtest feedback, 2026-07-23). Draw-free.
  const exitChunk = placed.find((p) => p.room === exitRoom);
  const byDepth = placed
    .filter((p) => p !== exitChunk)
    .flatMap((p) => p.anchors)
    .filter((p) => !(p.x === shop.x && p.y === shop.y))
    .filter((p) => !circleCandidates.some((c) => c.x === p.x && c.y === p.y))
    .filter((p) => dist[p.y * w + p.x]! >= 0)
    .sort((a, b) => dist[b.y * w + b.x]! - dist[a.y * w + a.x]!);
  const eventCandidates: TilePos[] = byDepth.slice(0, 1);
  const rest = byDepth.slice(1);
  while (rest.length > 0) {
    let bestI = 0;
    let bestD = -1;
    for (let i = 0; i < rest.length; i++) {
      const p = rest[i]!;
      let minD = Infinity;
      for (const q of eventCandidates) {
        const d = (q.x - p.x) * (q.x - p.x) + (q.y - p.y) * (q.y - p.y);
        if (d < minD) minD = d;
      }
      if (minD > bestD) {
        bestD = minD;
        bestI = i;
      }
    }
    eventCandidates.push(rest.splice(bestI, 1)[0]!);
  }

  return {
    w,
    h,
    tiles,
    rooms,
    entrance,
    exit,
    shop,
    circleCandidates,
    spawnRegions: rooms.filter((r) => r !== entryPlaced.room),
    pockets: pocketPlaced.map((p) => p.room),
    eventCandidates,
    archetype,
  };
}

// ---------------------------------------------------------------------------
// Cell-grid helpers

function edgeKey(a: number, b: number): number {
  return a < b ? a * 64 + b : b * 64 + a;
}

function openEdge(grid: CellGrid, a: number, b: number): void {
  grid.open.add(edgeKey(a, b));
}

function hasEdge(grid: CellGrid, a: number, b: number): boolean {
  return grid.open.has(edgeKey(a, b));
}

function neighbors(grid: CellGrid, cell: number): number[] {
  const gx = cell % grid.gw;
  const gy = (cell / grid.gw) | 0;
  const out: number[] = [];
  if (gy > 0) out.push(cell - grid.gw);
  if (gx < grid.gw - 1) out.push(cell + 1);
  if (gy < grid.gh - 1) out.push(cell + grid.gw);
  if (gx > 0) out.push(cell - 1);
  return out;
}

function freeNeighbors(grid: CellGrid, cell: number): number[] {
  return neighbors(grid, cell).filter(
    (c) => grid.carved[c] !== 1 && grid.grand[c] === -1,
  );
}

/**
 * Deterministic Dijkstra over cell weights. `blocked` (the entrance, once
 * used as a source) is never traversed. O(n²) — grids are ≤25 cells.
 */
function dijkstra(
  grid: CellGrid, weights: Float64Array, sources: number[], blocked: number,
): Float64Array {
  const n = grid.gw * grid.gh;
  const dist = new Float64Array(n).fill(Infinity);
  const done = new Uint8Array(n);
  for (const s of sources) dist[s] = 0;
  for (let iter = 0; iter < n; iter++) {
    let cur = -1;
    let best = Infinity;
    for (let i = 0; i < n; i++) {
      if (done[i] === 0 && dist[i]! < best) {
        best = dist[i]!;
        cur = i;
      }
    }
    if (cur < 0) break;
    done[cur] = 1;
    for (const nb of neighbors(grid, cur)) {
      if (nb === blocked && dist[nb] !== 0) continue;
      const d = dist[cur]! + weights[nb]!;
      if (d < dist[nb]!) dist[nb] = d;
    }
  }
  return dist;
}

/** Route from `sources` to the nearest of `targets`; carve + open edges. */
function routeTo(
  grid: CellGrid, weights: Float64Array,
  sources: number[], targets: number[], entranceCell: number,
): void {
  const blocked = sources.includes(entranceCell) ? -1 : entranceCell;
  const dist = dijkstra(grid, weights, sources, blocked);
  let goal = -1;
  let best = Infinity;
  for (const t of targets) {
    if (dist[t]! < best) {
      best = dist[t]!;
      goal = t;
    }
  }
  if (goal < 0 || best === Infinity) throw new Error('routeTo: unreachable');
  // Walk back along strictly-decreasing distance (deterministic neighbor order).
  let cur = goal;
  while (dist[cur]! > 0) {
    let next = -1;
    for (const nb of neighbors(grid, cur)) {
      if (nb === blocked && dist[nb] !== 0) continue;
      if (Math.abs(dist[nb]! + weights[cur]! - dist[cur]!) < 1e-9) {
        next = nb;
        break;
      }
    }
    if (next < 0) throw new Error('routeTo: broken parent chain');
    openEdge(grid, cur, next);
    grid.carved[cur] = 1;
    grid.carved[next] = 1;
    cur = next;
  }
}

function cellMask(grid: CellGrid, gx: number, gy: number): number {
  const cell = gy * grid.gw + gx;
  let mask = 0;
  if (gy > 0 && hasEdge(grid, cell, cell - grid.gw)) mask |= N;
  if (gx < grid.gw - 1 && hasEdge(grid, cell, cell + 1)) mask |= E;
  if (gy < grid.gh - 1 && hasEdge(grid, cell, cell + grid.gw)) mask |= S;
  if (gx > 0 && hasEdge(grid, cell, cell - 1)) mask |= W;
  return mask;
}

// ---------------------------------------------------------------------------
// Stamping

function stamp(tiles: Uint8Array, w: number, v: ChunkVariant, ox: number, oy: number): void {
  for (let y = 0; y < v.n; y++) {
    for (let x = 0; x < v.n; x++) {
      if (v.tiles[y * v.n + x] === 1) tiles[(oy + y) * w + ox + x] = 1;
    }
  }
}

function toRoom(v: ChunkVariant, ox: number, oy: number): Room {
  return {
    x: ox + v.bbox.x,
    y: oy + v.bbox.y,
    w: v.bbox.w,
    h: v.bbox.h,
    cx: ox + v.center.x,
    cy: oy + v.center.y,
  };
}

/**
 * Grand chunks are authored with all edge segments open; wall over every
 * aperture whose cell edge didn't open (seal-down). Sealed strips sit
 * against the neighbor's wall boundary, so they're ≥2 thick and
 * erosion-proof.
 */
function sealGrand(
  tiles: Uint8Array, w: number,
  grid: CellGrid, origin: { x: number; y: number; size: number }, ox: number, oy: number,
): void {
  const size = origin.size;
  for (let seg = 0; seg < size; seg++) {
    for (const side of ['n', 'e', 's', 'w'] as const) {
      const gx = origin.x + (side === 'n' || side === 's' ? seg : side === 'e' ? size - 1 : 0);
      const gy = origin.y + (side === 'e' || side === 'w' ? seg : side === 's' ? size - 1 : 0);
      const cell = gy * grid.gw + gx;
      const nb =
        side === 'n' ? (gy > 0 ? cell - grid.gw : -1)
        : side === 's' ? (gy < grid.gh - 1 ? cell + grid.gw : -1)
        : side === 'w' ? (gx > 0 ? cell - 1 : -1)
        : gx < grid.gw - 1 ? cell + 1 : -1;
      if (nb >= 0 && hasEdge(grid, cell, nb)) continue;
      for (let i = APERTURE_LO; i <= APERTURE_HI; i++) {
        const u = seg * CELL + i;
        const [x, y] =
          side === 'n' ? [ox + u, oy]
          : side === 's' ? [ox + u, oy + size * CELL - 1]
          : side === 'w' ? [ox, oy + u]
          : [ox + size * CELL - 1, oy + u];
        tiles[y * w + x] = 0;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Erosion

/**
 * Per-seed wobble. Candidacy is judged on a frozen snapshot; a wall tile may
 * become floor only when every wall direction opposite a floor neighbor is
 * wall 2 deep — a 1-thick divider (or a grand's sealed aperture) can never
 * be breached, so this cannot create unintended connections; erode-only
 * means it cannot pinch or disconnect anything either.
 */
function erode(tiles: Uint8Array, w: number, h: number, rng: Rng): void {
  const snap = tiles.slice();
  const wall = (x: number, y: number): boolean =>
    x < 0 || x >= w || y < 0 || y >= h || snap[y * w + x] === 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      if (snap[y * w + x] !== 0) continue;
      const dirs = [[0, -1], [1, 0], [0, 1], [-1, 0]] as const;
      let floors = 0;
      let thick = true;
      for (const [dx, dy] of dirs) {
        if (wall(x + dx, y + dy)) continue;
        floors++;
        // Opposite direction must be wall 2 deep.
        if (!wall(x - dx, y - dy) || !wall(x - 2 * dx, y - 2 * dy)) thick = false;
      }
      if (floors === 0 || floors > 2 || !thick) continue;
      if (rng.bool(floors === 2 ? ERODE_CORNER : ERODE_DENT)) tiles[y * w + x] = 1;
    }
  }
}
